import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from "react-router-dom";
import { QueryParameters } from '../utilities/query-parameters';
import { CrudFunctions } from '../utilities/crud-functions';
import { RenderingFunctions } from '../utilities/rendering-functions';
import GlobalDialog from './Modal/GlobalDialog';
import { BenchSheetParentListItem } from '../models/bench-sheet-parent-list-item';
import { BenchSheet } from '../models/benchsheet';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType, ProductState } from '../models/enums';

import DataGrid, {
    Column,
    Selection,
    HeaderFilter,
    Toolbar,
    Button as ColumnButton,
    Item as ToolbarItem,
    Paging,
    LoadPanel
} from 'devextreme-react/data-grid';

import { TextBox } from 'devextreme-react';
import { Button } from 'devextreme-react/button';
import { Template } from 'devextreme-react/core/template';

export default function BenchSheetLauncherForm() {
    const navigate = useNavigate();

    const [batches, setBatches] =
        useState<BenchSheetParentListItem[]>();

    const [selectedBatch, setSelectedBatch] =
        useState<BenchSheetParentListItem>();

    const [batchFilterString, setBatchFilterString] =
        useState<string>();

    const [benchSheets, setBenchSheets] =
        useState<BenchSheet[]>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getAllData();
    }, []);

    useEffect(() => {
        getBenchSheetList();
    }, [selectedBatch]);


    return (
        <div className="pw-content">
            <div className="lq-title-bar">
                <div className="lq-main-title">QC Bench Sheets Launcher</div>
                <div className="title-bar-spacer" />
                <div className="save-toolbar">
                    <Button id="goBackButton" text="" icon="arrowleft" type="normal"
                        hint="go back"
                        height={'2.6em'}
                        onClick={goBack} />
                </div>
            </div>

            <div className="pw-40-60-column-grid">
                <div id="bs-batch-search">
                    <div id="cl-batches">
                        <div id="cl-search-results">
                            <DataGrid
                                height="30rem"
                                wordWrapEnabled={true}
                                rowAlternationEnabled={true}
                                onSelectionChanged={searchResultsSelectionChanged}
                                dataSource={batches ? batches : []}
                                showBorders={true}>

                                <LoadPanel enabled={true} />
                                <Selection mode={'single'} />
                                <HeaderFilter visible={true} />
                                <Paging enabled={false} />

                                <Column dataField='batchName' width='35%' />
                                <Column dataField='partNumber' width='35%' cellRender={partNumberRender} />
                                <Column dataField='productState' width='30%' caption="Product State"
                                    cellRender={productStateRender} />

                                <Toolbar>
                                    <ToolbarItem location='before'>
                                        <div className="pw-datagrid-toolbar-template">
                                            <div className="pw-datagrid-toobar-title">Batches</div>
                                        </div>
                                    </ToolbarItem>
                                    <ToolbarItem location='after' template='searchTemplate' />

                                    <ToolbarItem location='after'>
                                        <Button type='success' text='Search' icon='find'
                                            hint='Search for matching Bench Sheets'
                                            width={'9em'}
                                            onClick={onSearchButtonClicked} />
                                    </ToolbarItem>
                                </Toolbar>

                                <Template name="searchTemplate" render={searchTemplateRender} />

                            </DataGrid>

                        </div>
                    </div>
                </div>

                <div id="bs-benchsheets">
                    <DataGrid
                        height="30rem"
                        width="100%"
                        wordWrapEnabled={true}
                        rowAlternationEnabled={true}
                        onRowDblClick={onBenchSheetsDatagridRowDoubleClick}
                        dataSource={benchSheets ? benchSheets : []}
                        showBorders={true}>

                        <LoadPanel enabled={true} />
                        <Selection mode={'single'} />
                        <HeaderFilter visible={true} />
                        <Paging enabled={false} />

                        <Column type="buttons" width='3rem'>
                            <ColumnButton render={gotoBenchSheetEditCellRender} />
                        </Column>

                        <Column dataField='benchSheetTechnologyName' caption="Technology" width='10rem' dataType="string" />
                        <Column dataField='createTimestamp' caption="Created" width='7rem'
                            dataType="date" format="MM/dd/yyyy" />
                        <Column dataField='scopeTypesString' caption="Applicable Characterizations" dataType="string" />

                        <Toolbar>
                            <ToolbarItem location='before'>
                                <div className="pw-datagrid-toolbar-template">
                                    <div className="pw-datagrid-toobar-title">QC Bench Sheets</div>
                                </div>
                            </ToolbarItem>
                        </Toolbar>

                    </DataGrid>
                </div>
            </div>

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    function onSearchButtonClicked() {
        if (batchFilterString)
            getBatchesList(batchFilterString);
    }

    function onBenchSheetsDatagridRowDoubleClick(e: any) {
        if (e.data) {
            let benchSheet = e.data as BenchSheet;

            if (benchSheet) {
                let path = `/benchsheet/${benchSheet.benchSheetTechnology}/${benchSheet.benchSheetId}`;
                navigate(path);
            }
        }
    }

    function searchResultsSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0 &&
            e.selectedRowsData[0].batchId) {
            setSelectedBatch(e.selectedRowsData[0]);
        }
    }

    function batchFilterChanged(e: any) {
        setBatchFilterString(e.value);

        if (e.value)
            QueryParameters.setSearchTerm(e.value);
    }

    // crud functions

    async function getBatchesList(filter: string) {
        if (filter) {
            await CrudFunctions.getData(`benchsheets/benchsheetparentlist/${filter}`)
                .then((data) => {
                    console.log("Batches List");
                    console.log(data);
                    setBatches(data);
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Retrieving Batch List");
                });
        }
    }

    async function getBenchSheetList() {
        if (selectedBatch) {
            const batchId = selectedBatch.batchId;

            let url = `benchsheets/benchsheetsandtechnologiesbybatchid/${batchId}`;

            await CrudFunctions.getData(url)
                .then((data) => {
                    setBenchSheets(data);
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Retrieving Bench Sheet List");
                });
        }
    }

    function getAllData() {
        const batchFilter = QueryParameters.getSearchTerm();

        if (batchFilter.length > 0) {
            setBatchFilterString(batchFilter);
            getBatchesList(batchFilter);
        }
    }

    //navigate

    function goBack() {
        history.back();
    }

    //render

    function gotoBenchSheetEditCellRender(e: any) {
        let benchSheet = e.data as BenchSheet;

        if (benchSheet && benchSheet.benchSheetId && selectedBatch) {
            let path = `/benchsheet/${benchSheet.benchSheetTechnology}/${benchSheet.benchSheetId}`;

            return <NavLink to={path} className="datagrid-icon-link"><span className="dx-icon-edit datagrid-icon column-button-margin"></span></NavLink>;
        }
    }

    function searchTemplateRender(e: any) {
        return (
            <div className="pw-datagrid-toolbar-template">
                <TextBox id="txtSearch" className="pw-datagrid-toobar-textbox"
                    placeholder="search"
                    hint="search by bench Sheet"
                    value={batchFilterString}
                    onValueChanged={batchFilterChanged}
                    onEnterKey={onSearchButtonClicked} />
            </div>
        );
    }

    function renderBenchSheetStartedColumn(item: BenchSheet) {
        if (item.createTimestamp)
            return (
                <div className="bench-sheet-datagrid-status-icon"><i className="dx-icon-runner" /></div>
            );
        else
            return (
                <div className="bench-sheet-datagrid-status-icon"><i className="dx-icon-preferences" /></div>
            );
    }

    function partNumberRender(e: any) {
        let item = e.data as BenchSheetParentListItem;
        let cssClass = RenderingFunctions.returnProductStateColor(item.productState);

        if (item)
            return <div id={`batch_${item.batchId}`} className={cssClass}>{item.partNumber}</div>;
        else
            return <div></div>;
    }

    function productStateRender(e: any) {
        let item = e.data as BenchSheetParentListItem;
        let cssClass = RenderingFunctions.returnProductStateColor(item.productState);

        if (item)
            return <div className={cssClass}>{ProductState[item.productState as ProductState]}</div>;
        else
            return <div></div>;
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

}
